import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { createContext, useState, useEffect } from 'react';
import { getAnalytics, eventHub } from '@mirohq-internal/website-analytics';
import { SplitIoProvider } from '@mirohq-internal/website-splitio';
import { useBehaviourAutoTracking } from '@mirohq-internal/website-web-tracking';
import { getOneTrustDataDomainScript } from '../onetrust/onetrust.esm.js';
import { initializeCookieAnalytics } from '@mirohq-internal/website-cookie-consent';

const ServicesContext = /*#__PURE__*/ createContext({
    analytics: undefined,
    eventHub: undefined
});
const userActions = [
    'mousemove',
    'keydown',
    'click',
    'touchstart'
];
const ServicesProvider = ({ children, withAnalytics = true, withUserTracking = false, withSplitIo = true, ethnioId, splitAttributes = {} })=>{
    const router = useRouter();
    useBehaviourAutoTracking(withUserTracking);
    const [oneTrustIsLoaded, setOneTrustIsLoaded] = useState(false);
    const [loadOneTrust, setLoadOneTrust] = useState(false);
    const [loadAnalytics, setLoadAnalytics] = useState(false);
    // Directly load Split IO to avoid screen flickering with experiments above the fold
    const loadSplitIo = withSplitIo && !!process.env.NEXT_PUBLIC_SPLITIO_CLIENT_KEY;
    // GTM
    const analytics = getAnalytics();
    // Add listener to trigger "page view" at route change
    useEffect(()=>{
        if (!eventHub) {
            return;
        }
        // Trigger page view for initial load
        eventHub.page();
        // Setup callback for route changes
        router.events.on('routeChangeComplete', eventHub.page);
        // Remove event tracking when provider is unmounted
        return ()=>{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            router.events.off('routeChangeComplete', eventHub?.page);
        };
    }, [
        router.events
    ]);
    // Add listener to load `OneTrust`
    useEffect(()=>{
        for (const action of userActions){
            window.addEventListener(action, handleUserAction);
        }
        // Return clean-up function
        return removeInteractionListeners;
    }, []);
    // When One Trust is loaded, enable analytics to be loaded
    useEffect(()=>{
        if (!oneTrustIsLoaded) {
            return;
        }
        setLoadAnalytics(withAnalytics);
    }, [
        withAnalytics,
        oneTrustIsLoaded
    ]);
    // Initialize analytics providers
    useEffect(()=>{
        if (loadAnalytics && analytics) {
            analytics.initialize();
        }
    }, [
        loadAnalytics,
        analytics
    ]);
    // Handler initial user action
    const handleUserAction = ()=>{
        // Enable One Trust script to be loaded
        setLoadOneTrust(true);
        // Remove listeners to avoid repeating this function
        removeInteractionListeners();
    };
    // Clean up function to remove the user action listeners
    const removeInteractionListeners = ()=>{
        for (const action of userActions){
            window.removeEventListener(action, handleUserAction);
        }
    };
    const handleOneTrustOnLoad = ()=>{
        initializeCookieAnalytics();
        // Update flag to load other scripts only when OneTrust is initialized
        const checkOneTrustInit = setInterval(()=>{
            if (window.OneTrust) {
                setOneTrustIsLoaded(true);
                window.dispatchEvent(new CustomEvent('oneTrustLoaded'));
                // Clear interval to stop looping
                clearInterval(checkOneTrustInit);
            }
        }, 300);
    };
    let content = jsxs(Fragment, {
        children: [
            jsx(Script, {
                strategy: "beforeInteractive",
                // Removing next/script from first render negatively impacts performance.
                // We are explicitly using CDN host to load optanonWrapper using next/script.
                src: "https://mirostatic.com/website/services-provider-scripts/optanonWrapper.js"
            }),
            loadOneTrust && jsx(Script, {
                src: "https://cdn.cookielaw.org/consent/4133a0ef-6999-461d-909d-24f2b05bbc15/otSDKStub.js",
                "data-document-language": "true",
                charSet: "UTF-8",
                "data-domain-script": getOneTrustDataDomainScript(),
                onLoad: handleOneTrustOnLoad
            }),
            ethnioId && jsx(Script, {
                async: true,
                id: "ethnio",
                type: "text/javascript",
                strategy: "afterInteractive",
                src: `//ethn.io/${ethnioId}.js`
            }),
            children
        ]
    });
    if (loadSplitIo) {
        content = jsx(SplitIoProvider, {
            attributes: splitAttributes,
            children: content
        });
    }
    return(// eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    jsx(ServicesContext.Provider, {
        value: {
            analytics,
            eventHub
        },
        children: content
    }));
};

export { ServicesContext, ServicesProvider };
